import { A11yModule } from '@angular/cdk/a11y';
import { DialogRef } from '@angular/cdk/dialog';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';
import { debounceTime, fromEvent, Subject, take, tap, timer } from 'rxjs';

import { DeviceCallComponent } from '@app/features/device/device-call';
import { DeviceNamePipe } from '@app/features/device/pipes/device-name.pipe';
import { scaleToContainRectangle } from '@app/shared/utils/screen.utils';

import { FlatButtonComponent } from '../../../shared/components/buttons/flat-button/flat-button.component';
import { AlarmSosDialogNotificationService } from './alarm-sos-dialog-notification.service';

interface AlarmSosDialogData {
  deviceId: string;
  deviceName?: string | null;
  userName?: string | null;
}

@Component({
  selector: 'app-alarm-sos-dialog',
  standalone: true,
  imports: [FlatButtonComponent, RouterLink, MatDialogModule, A11yModule, DeviceCallComponent],
  templateUrl: './alarm-sos-dialog.component.html',
  styleUrl: './alarm-sos-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DeviceNamePipe],
})
export class AlarmSosDialogComponent implements OnInit, AfterViewInit {
  readonly interaction$ = new Subject<void>();

  protected deviceName: string;

  protected $call = signal(false);

  protected readonly $callWidth = signal('90vw');

  protected readonly data = inject<AlarmSosDialogData>(MAT_DIALOG_DATA);

  protected readonly $interactionDisabled = signal(true);

  private readonly dialogRef = inject(DialogRef);

  private readonly deviceNamePipe = inject(DeviceNamePipe);

  private readonly notificationService = inject(AlarmSosDialogNotificationService);

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.deviceName =
      this.deviceNamePipe.transform({
        id: this.data.deviceId,
        name: this.data.deviceName || undefined,
        userName: this.data.userName || undefined,
      }) || this.data.deviceId;
  }

  public static open(dialog: MatDialog, data: AlarmSosDialogData): MatDialogRef<AlarmSosDialogComponent, void> {
    return dialog.open(AlarmSosDialogComponent, {
      data,
      panelClass: 'alarm-sos-dialog',
      disableClose: true,
      autoFocus: false,
    });
  }

  @HostListener('click')
  public handleDialogClick(): void {
    this.notificationService.stop();
    this.interaction$.next();
  }

  public ngOnInit(): void {
    timer(500)
      .pipe(
        take(1),
        tap(() => this.$interactionDisabled.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public ngAfterViewInit(): void {
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(100),
        tap(() => this.recalculateCallSize()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.recalculateCallSize();
  }

  public startCall(): void {
    this.$call.set(true);
  }

  public close(): void {
    this.dialogRef.close();
  }

  protected recalculateCallSize(): void {
    const rectWidth = window.innerWidth * 0.9;
    const rectHeight = window.innerHeight * 0.9;

    if (!rectWidth) {
      this.$callWidth.set('90vw');
      return;
    }

    const targetWidth = rectWidth * 0.9;
    const targetHeight = targetWidth * 0.75 + 300;

    const { width, scale } = scaleToContainRectangle(rectWidth, rectHeight, targetWidth, targetHeight);

    if (scale >= 1) {
      this.$callWidth.set('90vw');
    } else {
      this.$callWidth.set(`${width}px`);
    }
  }
}
